import { z } from 'zod'

/**
 * Notifications internes (docs/notifications/README.md). Trois concepts distincts : la notification personnelle
 * (cette liste), la situation à traiter (compteurs des modules) et l'historique métier (journaux des modules). Lire
 * ou archiver une notification ne modifie aucune donnée métier.
 */

/** Catégorie = clé de module (`app_module_settings`). */
export const notificationCategories = [
  'soumissions',
  'contrats',
  'facturation',
  'courriels',
  'alertes_sms',
  'missions',
  'routes',
] as const
export type NotificationCategory = (typeof notificationCategories)[number]

export const notificationTypes = [
  'LEAD_CREATED',
  'LEAD_REMINDER_DUE',
  'SMS_RECEIVED',
  'SMS_SEND_FAILED',
  'EMAIL_RECEIVED',
  'PAYMENT_RECORDED',
  'INVOICE_OVERDUE',
  'CONTRACT_SIGNED',
  'CONTRACT_DECLINED',
  'MISSION_STARTED',
  'MISSION_ENDED',
  'MISSION_ENDED_UNRESOLVED',
  'MISSION_PROBLEM_REPORTED',
] as const
export type NotificationType = (typeof notificationTypes)[number]

export type NotificationPriority = 'ALERT' | 'INFO'

export const notificationCategoryLabels: Record<NotificationCategory, string> = {
  soumissions: 'Demandes',
  contrats: 'Contrats',
  facturation: 'Facturation',
  courriels: 'Courriels',
  alertes_sms: 'Alertes SMS',
  missions: 'Missions',
  routes: 'Routes',
}

const notificationRowSchema = z.object({
  id: z.string(),
  // Un type inconnu (serveur plus récent) reste affichable avec un titre neutre.
  type: z.string(),
  category: z.enum(notificationCategories),
  priority: z.enum(['ALERT', 'INFO']),
  groupCount: z.number().int().positive(),
  createdAt: z.string(),
  lastEventAt: z.string(),
  readAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  accessible: z.boolean(),
  reference: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
  destination: z.string().nullable(),
  resolved: z.boolean(),
})
export type NotificationRow = z.infer<typeof notificationRowSchema>

export const notificationPageSchema = z.object({
  items: z.array(notificationRowSchema),
  total: z.number().int(),
  counts: z.object({ all: z.number().int(), unread: z.number().int(), archived: z.number().int() }),
  limit: z.number().int(),
  offset: z.number().int(),
})
export type NotificationPage = z.infer<typeof notificationPageSchema>

/** Compteurs des modules : clé absente = badge absent (permission ou module), jamais un zéro inventé. */
export const notificationSummarySchema = z.object({
  unread: z.number().int(),
  badges: z.object({
    soumissions: z.number().int().optional(),
    routes: z.number().int().optional(),
    missions: z.number().int().optional(),
    facturation: z.number().int().optional(),
    courriels: z.number().int().optional(),
    alertes_sms: z.number().int().optional(),
  }),
  routesSeason: z.string(),
  generatedAt: z.string(),
})
export type NotificationSummary = z.infer<typeof notificationSummarySchema>
export type NavigationBadgeKey = keyof NotificationSummary['badges']

export const notificationPreferencesSchema = z.array(
  z.object({
    category: z.enum(notificationCategories),
    infoEnabled: z.boolean(),
    alertEnabled: z.boolean(),
    hasInfo: z.boolean(),
    hasAlert: z.boolean(),
  }),
)
export type NotificationPreference = z.infer<typeof notificationPreferencesSchema>[number]

export type NotificationTab = 'ALL' | 'UNREAD' | 'ARCHIVED'
export type NotificationPeriod = 'TODAY' | '7D' | '30D' | ''

export interface NotificationFilter {
  tab: NotificationTab
  category: NotificationCategory | ''
  priority: NotificationPriority | ''
  period: NotificationPeriod
  limit: number
  offset: number
}

/** Portée de « Marquer comme lues » : les filtres de la page, sans l'onglet ni la pagination. */
export type NotificationScope = Pick<NotificationFilter, 'category' | 'priority' | 'period'>

function plural(count: number, one: string, many: string): string {
  return count > 1 ? many : one
}

function numberFrom(data: Record<string, unknown>, key: string): number | null {
  const value = data[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** Titre court et concret. Un groupe dit le nombre d'événements arrivés depuis la dernière lecture. */
export function notificationTitle(row: Pick<NotificationRow, 'type' | 'groupCount' | 'data'>): string {
  const count = row.groupCount
  switch (row.type as NotificationType) {
    case 'LEAD_CREATED':
      return 'Nouvelle demande de déneigement'
    case 'LEAD_REMINDER_DUE':
      return 'Rappel arrivé'
    case 'SMS_RECEIVED':
      return count > 1 ? `${String(count)} nouveaux SMS dans cette conversation` : 'Nouveau SMS'
    case 'SMS_SEND_FAILED':
      return count > 1 ? `Envoi SMS échoué pour ${String(count)} messages` : 'Envoi SMS échoué'
    case 'EMAIL_RECEIVED':
      return count > 1
        ? `${String(count)} nouveaux courriels dans une conversation`
        : 'Nouveau courriel dans une conversation'
    case 'PAYMENT_RECORDED':
      return 'Paiement reçu'
    case 'INVOICE_OVERDUE':
      return 'Facture devenue en retard'
    case 'CONTRACT_SIGNED':
      return 'Contrat signé'
    case 'CONTRACT_DECLINED':
      return 'Refus reçu'
    case 'MISSION_STARTED':
      return 'Mission démarrée'
    case 'MISSION_ENDED':
      return 'Mission terminée'
    case 'MISSION_ENDED_UNRESOLVED': {
      const unresolved = numberFrom(row.data, 'unresolved')
      return unresolved
        ? `Mission terminée avec ${String(unresolved)} ${plural(unresolved, 'résidence', 'résidences')} à vérifier`
        : 'Mission terminée avec des résidences à vérifier'
    }
    case 'MISSION_PROBLEM_REPORTED':
      return count > 1 ? `${String(count)} problèmes signalés` : 'Problème signalé'
    default:
      return 'Notification'
  }
}

/** Seconde ligne quand la référence seule ne suffit pas. */
export function notificationDetail(row: Pick<NotificationRow, 'type'>): string | null {
  if (row.type === 'SMS_SEND_FAILED') return 'Ouvre le journal des envois.'
  return null
}

export type NotificationTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral'

/** Un paiement reçu est une information positive; une facture en retard, une alerte. */
export function notificationTone(
  row: Pick<NotificationRow, 'type' | 'priority' | 'resolved'>,
): NotificationTone {
  if (row.resolved) return 'neutral'
  switch (row.type as NotificationType) {
    case 'PAYMENT_RECORDED':
    case 'CONTRACT_SIGNED':
      return 'success'
    case 'INVOICE_OVERDUE':
    case 'SMS_SEND_FAILED':
      return 'danger'
    case 'MISSION_ENDED_UNRESOLVED':
    case 'MISSION_PROBLEM_REPORTED':
    case 'LEAD_REMINDER_DUE':
    case 'CONTRACT_DECLINED':
      return 'warning'
    case 'LEAD_CREATED':
    case 'SMS_RECEIVED':
    case 'EMAIL_RECEIVED':
    case 'MISSION_STARTED':
    case 'MISSION_ENDED':
      return 'info'
    default:
      // Type inconnu (serveur plus récent).
      return row.priority === 'ALERT' ? 'warning' : 'info'
  }
}

/** Destinations admises : chemins internes produits par le serveur, jamais une URL arbitraire. */
const destinationPattern =
  /^\/(leads|invoices|contracts|missions)\/[0-9a-f-]{36}$|^\/sms\?onglet=(reception&conversation=[0-9a-f-]{36}|journal)$|^\/emails\?thread=[0-9a-f-]{36}$|^\/facturation\?view=paiements$/

export function safeDestination(destination: string | null): string | null {
  return destination && destinationPattern.test(destination) ? destination : null
}

export type NotificationDay = 'Aujourd’hui' | 'Hier' | 'Plus anciennes'

const torontoDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Toronto',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function civilDay(date: Date): number {
  const [year, month, day] = torontoDate.format(date).split('-').map(Number)
  return Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) / 86_400_000
}

export function notificationDay(lastEventAt: string, now: Date): NotificationDay {
  const diff = civilDay(now) - civilDay(new Date(lastEventAt))
  if (diff <= 0) return 'Aujourd’hui'
  if (diff === 1) return 'Hier'
  return 'Plus anciennes'
}

/** Groupes Aujourd'hui / Hier / Plus anciennes, dans l'ordre reçu (le plus récent d'abord). */
export function groupByDay(
  rows: readonly NotificationRow[],
  now: Date,
): { label: NotificationDay; items: NotificationRow[] }[] {
  const order: NotificationDay[] = ['Aujourd’hui', 'Hier', 'Plus anciennes']
  return order
    .map((label) => ({ label, items: rows.filter((row) => notificationDay(row.lastEventAt, now) === label) }))
    .filter((group) => group.items.length > 0)
}

const exactFormat = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'America/Toronto',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const shortDayFormat = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'America/Toronto',
  day: 'numeric',
  month: 'short',
})

export function exactTime(iso: string): string {
  return exactFormat.format(new Date(iso))
}

/** Heure relative courte : « à l'instant », « 12 min », « 2 h », « hier », « 14 oct. ». */
export function relativeTime(iso: string, now: Date): string {
  const date = new Date(iso)
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000)
  if (minutes < 1) return 'à l’instant'
  if (minutes < 60) return `${String(minutes)} min`
  const day = notificationDay(iso, now)
  if (day === 'Aujourd’hui') return `${String(Math.floor(minutes / 60))} h`
  if (day === 'Hier') return 'hier'
  return shortDayFormat.format(date)
}

/** Compteur compact : `99+` au-delà; le nombre exact reste dans le libellé accessible. */
export function compactCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}

export function unreadLabel(count: number): string {
  return `${String(count)} ${plural(count, 'non lue', 'non lues')}`
}

export function bellLabel(unread: number | null, stale: boolean): string {
  if (unread === null) return 'Notifications'
  const base = unread > 0 ? `Notifications, ${unreadLabel(unread)}` : 'Notifications, aucune non lue'
  return stale ? `${base} (dernière valeur connue)` : base
}

const priorityScopeLabels: Record<NotificationPriority, string> = { ALERT: 'alertes', INFO: 'informations' }
const periodLabels: Record<Exclude<NotificationPeriod, ''>, string> = {
  TODAY: 'aujourd’hui',
  '7D': '7 derniers jours',
  '30D': '30 derniers jours',
}

/** Libellé de l'action de masse : sa portée exacte quand un filtre est actif. */
export function markAllLabel(scope: NotificationScope, unreadInScope: number): string {
  const parts = [
    scope.category ? notificationCategoryLabels[scope.category] : null,
    scope.priority ? priorityScopeLabels[scope.priority] : null,
    scope.period ? periodLabels[scope.period] : null,
  ].filter((part): part is string => part !== null)
  if (parts.length === 0) return 'Tout marquer comme lu'
  return `Marquer comme lues (${String(unreadInScope)}) · ${parts.join(', ')}`
}

export function markedReadMessage(count: number, scope: NotificationScope): string {
  const parts = markAllLabel(scope, count).split(' · ')[1]
  const suffix = parts ? ` (${parts})` : ''
  return `${String(count)} ${plural(count, 'notification marquée comme lue', 'notifications marquées comme lues')}${suffix}. Aucun dossier n’est fermé.`
}

/** Badges de navigation : unité et ton de chaque file (danger = factures en retard, warning = terrain). */
export const navigationBadges: Record<
  NavigationBadgeKey,
  { href: string; tone: 'info' | 'warning' | 'danger'; unit: (count: number, season: string) => string }
> = {
  soumissions: {
    href: '/soumissions',
    tone: 'info',
    unit: (count) => `${String(count)} ${plural(count, 'dossier à traiter', 'dossiers à traiter')}`,
  },
  routes: {
    href: '/routes',
    tone: 'warning',
    unit: (count, season) =>
      `${String(count)} ${plural(count, 'résidence sans route', 'résidences sans route')} · saison ${season}`,
  },
  missions: {
    href: '/missions',
    tone: 'warning',
    unit: (count) =>
      `${String(count)} ${plural(count, 'mission nécessite', 'missions nécessitent')} une intervention`,
  },
  facturation: {
    href: '/facturation',
    tone: 'danger',
    unit: (count) =>
      `${String(count)} ${plural(count, 'facture en retard', 'factures en retard')} · toutes saisons`,
  },
  courriels: {
    href: '/emails',
    tone: 'info',
    unit: (count) => `${String(count)} ${plural(count, 'conversation non lue', 'conversations non lues')}`,
  },
  alertes_sms: {
    href: '/sms',
    tone: 'info',
    unit: (count) => `${String(count)} ${plural(count, 'conversation non lue', 'conversations non lues')}`,
  },
}

/** Lien du badge : la file filtrée correspondant exactement au compteur. */
export function badgeDestination(key: NavigationBadgeKey, season: string): string {
  switch (key) {
    case 'soumissions':
      // La pastille est sur « Demandes », qui ouvre la liste V3 depuis le 31 oct. 2026 (même valeur de vue).
      return '/demandes-v3?vue=maintenant'
    case 'routes':
      return `/routes/editor?saison=${season}`
    case 'missions':
      return '/missions?onglet=historique&statut=a-verifier'
    case 'facturation':
      return '/facturation?view=factures&vue=overdue'
    case 'courriels':
      return '/emails?vue=non-lus'
    case 'alertes_sms':
      return '/sms?onglet=reception&vue=non-lus'
  }
}

export const preferenceTexts: Record<NotificationCategory, { info: string | null; alert: string | null }> = {
  soumissions: { info: 'Nouvelle demande', alert: 'Rappel arrivé' },
  contrats: { info: 'Contrat signé', alert: 'Refus reçu' },
  facturation: { info: 'Paiement reçu', alert: 'Facture en retard' },
  courriels: { info: 'Nouveau courriel dans une conversation', alert: null },
  alertes_sms: { info: 'Nouveau SMS', alert: 'Échec d’envoi nécessitant une action' },
  missions: {
    info: 'Mission démarrée ou terminée',
    alert: 'Problème signalé, fin avec résidences à vérifier',
  },
  routes: { info: null, alert: null },
}
